import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Bot, Globe, Code, Copy, Check, User } from 'lucide-react';
import { apiClient } from '../apiClient';

function PublicInstructionPage() {
  const { instructionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backTo = location.state?.backTo || '/library';
  
  const [instruction, setInstruction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const loadInstruction = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let data = null;
      try {
        data = await apiClient.get(`/api/agent-instructions/${instructionId}`);
      } catch (_) {
        data = await apiClient.get(`/api/public/agent-instructions/${instructionId}`);
      }
      setInstruction(data);
    } catch (err) {
      setError(err.message || 'Failed to load instruction');
    } finally {
      setLoading(false);
    }
  }, [instructionId]);

  useEffect(() => {
    loadInstruction();
  }, [loadInstruction]);

  const copyTemplate = () => {
    if (instruction?.instruction_template) {
      navigator.clipboard.writeText(instruction.instruction_template);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error || !instruction) {
    return (
      <div className="min-h-screen p-6">
        <div className="card-glass max-w-3xl mx-auto text-center">
          <Bot className="w-12 h-12 mx-auto mb-4 opacity-40" />
          <h1 className="text-2xl font-bold mb-2">Instruction Not Found</h1>
          <p className="text-gray-400 mb-6">{error || 'This instruction may be private or deleted.'}</p>
          <button className="btn-primary" onClick={() => navigate(backTo)}>
            Back to Library
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate(backTo)}
          className="text-gray-400 hover:text-white mb-6 flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Library
        </button>

        <div className="card-glass mb-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                <Bot className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">{instruction.name}</h1>
                {instruction.creator && (
                  <div className="flex items-center gap-2 text-sm text-gray-400 mt-1">
                    <User className="w-4 h-4" />
                    <span>by {instruction.creator.username || instruction.creator.name}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {instruction.live_enabled && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-green-500/20 text-green-300 border border-green-500/30">
                  <Globe className="w-3 h-3" />
                  Live
                </span>
              )}
              {instruction.dev_enabled && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">
                  <Code className="w-3 h-3" />
                  Dev
                </span>
              )}
            </div>
          </div>

          {instruction.description && (
            <p className="text-gray-400 mb-6">{instruction.description}</p>
          )}

          {instruction.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {instruction.tags.map((tag, i) => (
                <span key={i} className="px-3 py-1 rounded-full text-sm bg-white/10 text-gray-300">
                  {tag}
                </span>
              ))}
            </div>
          )}

          <div className="relative">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-400">INSTRUCTION TEMPLATE</h3>
              <button
                className="flex items-center gap-1 text-sm text-cyan-400 hover:text-cyan-300"
                onClick={copyTemplate}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <pre className="p-4 rounded-lg bg-black/30 border border-white/10 text-sm font-mono whitespace-pre-wrap overflow-x-auto max-h-[500px] overflow-y-auto">
              {instruction.instruction_template}
            </pre>
          </div>

          {instruction.variables?.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-400 mb-3">VARIABLES ({instruction.variables.length})</h3>
              <div className="grid gap-3">
                {instruction.variables.map((v) => (
                  <div key={v.variable_name} className="p-3 rounded-lg bg-white/5 border border-white/10">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono text-cyan-400">{`{{${v.variable_name}}}`}</span>
                      <span className="text-xs text-gray-400 bg-white/10 px-2 py-0.5 rounded">{v.input_type}</span>
                    </div>
                    {v.display_name && v.display_name !== v.variable_name && (
                      <p className="text-sm text-white">{v.display_name}</p>
                    )}
                    {v.description && (
                      <p className="text-sm text-gray-400">{v.description}</p>
                    )}
                    {v.default_value && (
                      <p className="text-xs text-gray-500 mt-1">Default: {v.default_value}</p>
                    )}
                    {v.dropdown_options?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {v.dropdown_options.map((opt, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 rounded bg-white/10 text-gray-300">{opt}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="card-glass">
          <h2 className="text-lg font-bold mb-4">Want to use this instruction?</h2>
          <p className="text-gray-400 mb-4">
            Sign up or log in to create your own version, or use the template as inspiration for your own AI agents.
          </p>
          <div className="flex gap-3">
            <button className="btn-primary" onClick={() => navigate('/pricing')}>
              Get Started
            </button>
            <button className="btn-secondary" onClick={() => navigate('/login')}>
              Log In
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PublicInstructionPage;
